import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import useScrollToTop from "@/hooks/useScrollToTop";
import { useEffect, useState, lazy, Suspense, startTransition } from "react";
import CloudPageSkeleton from "@/components/CloudPageSkeleton";
import Navigation from "@/components/Navigation";
import { ChunkErrorBoundary } from "@/components/ChunkErrorBoundary";

// Lazy load non-critical UI components
const CookieConsent = lazy(() => import("@/components/CookieConsent").then(m => ({ default: m.CookieConsent })));
const BackToTop = lazy(() => import("@/components/BackToTop"));

// Lazy load all page components for code splitting
const Index = lazy(() => import("./pages/Index"));
const OurWork = lazy(() => import("./pages/OurWork"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Textmi = lazy(() => import("./pages/Textmi"));
const ChildSafetyStandards = lazy(() => import("./pages/ChildSafetyStandards"));
const TextmiPrivacyPolicy = lazy(() => import("./pages/TextmiPrivacyPolicy"));
const CustomDevelopment = lazy(() => import("./pages/services/CustomDevelopment"));
const MVPDevelopment = lazy(() => import("./pages/services/MVPDevelopment"));

const CloudSolutions = lazy(() => import("./pages/services/CloudSolutions"));
const DataVisualisation = lazy(() => import("./pages/services/DataVisualisation"));
const InternshipRegistration = lazy(() => import("./pages/InternshipRegistration"));

const Internship = lazy(() => import("./pages/Internship"));
const About = lazy(() => import("./pages/About"));


const POSPlus = lazy(() => import("./pages/work/POSPlus"));
const School360 = lazy(() => import("./pages/work/School360"));
const Contact = lazy(() => import("./pages/Contact"));
const AmazonAWS = lazy(() => import("./pages/cloud/AmazonAWS"));
const GoogleCloud = lazy(() => import("./pages/cloud/GoogleCloud"));
const MicrosoftAzure = lazy(() => import("./pages/cloud/MicrosoftAzure"));
const PaymentStatus = lazy(() => import("./pages/PaymentStatus"));

const queryClient = new QueryClient();

const AppContent = () => {
  useScrollToTop();
  const location = useLocation();
  const [displayLocation, setDisplayLocation] = useState(location);
  const [transitionStage, setTransitionStage] = useState<'fade-in' | 'fade-out'>('fade-in');

  useEffect(() => {
    if (location.pathname !== displayLocation.pathname) {
      // Use startTransition for non-urgent UI update
      startTransition(() => {
        setTransitionStage('fade-out');
      });
    }
  }, [location, displayLocation]);

  const onAnimationEnd = () => {
    if (transitionStage === 'fade-out') {
      startTransition(() => {
        setDisplayLocation(location);
        setTransitionStage('fade-in');
      });
    }
  };

  return (
    <>
      <Navigation />
      <div
        className={`page-transition ${transitionStage}`}
        onAnimationEnd={onAnimationEnd}
      >
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
          <Routes location={displayLocation}>
        <Route path="/" element={<Index />} />
        <Route path="/about" element={<About />} />
        <Route path="/our-work" element={<OurWork />} />
        <Route path="/internship" element={<Internship />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/textmi" element={<Textmi />} />
        <Route path="/textmi/privacy-policy" element={<TextmiPrivacyPolicy />} />
        <Route path="/textmi/child-safety-standards" element={<ChildSafetyStandards />} />
        <Route path="/internship-registration" element={<InternshipRegistration />} />
        <Route path="/services/custom-development" element={<CustomDevelopment />} />
        <Route path="/services/mvp-development" element={<MVPDevelopment />} />
        <Route path="/services/cloud-solutions" element={<CloudSolutions />} />
        <Route path="/services/data-visualisation" element={<DataVisualisation />} />
        {/* Redirects for merged service pages */}
        <Route path="/services/custom-software" element={<Navigate to="/services/custom-development" replace />} />
        <Route path="/services/data-engineering" element={<Navigate to="/services/data-visualisation" replace />} />
        <Route path="/services/enterprise-solutions" element={<Navigate to="/services/custom-development" replace />} />
        <Route path="/services/ai-integrated" element={<Navigate to="/services/custom-development" replace />} />
        <Route path="/services/mobile-development" element={<Navigate to="/services/custom-development" replace />} />
        {/* Redirects for removed technology pages */}
        <Route path="/technologies/react-angular" element={<Navigate to="/services/custom-development" replace />} />
        <Route path="/technologies/nodejs-python" element={<Navigate to="/services/custom-development" replace />} />
        <Route path="/technologies/mobile-apps" element={<Navigate to="/services/custom-development" replace />} />
        <Route path="/technologies/artificial-intelligence" element={<Navigate to="/services/custom-development" replace />} />
        <Route path="/technologies/aws-azure" element={<Navigate to="/services/cloud-solutions" replace />} />
        <Route path="/technologies/devops" element={<Navigate to="/services/cloud-solutions" replace />} />
        
        
        
        <Route path="/work/hotel-manager" element={<Navigate to="/our-work" replace />} />
        <Route path="/work/pos-plus" element={<POSPlus />} />
        <Route path="/work/school-360" element={<School360 />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/cloud/amazon-aws" element={<Suspense fallback={<CloudPageSkeleton />}><AmazonAWS /></Suspense>} />
        <Route path="/cloud/google-cloud" element={<Suspense fallback={<CloudPageSkeleton />}><GoogleCloud /></Suspense>} />
        <Route path="/cloud/microsoft-azure" element={<Suspense fallback={<CloudPageSkeleton />}><MicrosoftAzure /></Suspense>} />
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="/payment/status" element={<PaymentStatus />} />
        <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      </div>
    </>
  );
};

const App = () => {
  return (
    <ChunkErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <Suspense fallback={null}>
            <CookieConsent />
          </Suspense>
          <BrowserRouter>
            <AppContent />
            <Suspense fallback={null}>
              <BackToTop />
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </ChunkErrorBoundary>
  );
};

export default App;
